import { useMemo } from 'react';
import { createSearchParams, useSearchParams } from 'react-router-dom';

import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { ValleyBankPermissionContext } from '~/core/routes/valley-bank/types';
import { getUserRoleTypeActionPermissions } from '~/valleyBank/domains/page/hooks/useUserRoleTypePermissions';
import { useCanToggleDataSource } from '~/valleyBank/domains/page/ui/ValleyBankDataMedataHeader';

// Exports
export const useValleyGlobalNavConfig = (): ValleyBankPermissionContext & {
  dashboardModeQueryParam: string;
  hasSubheader: boolean;
} => {
  const { user } = useUserAuthOrThrow();

  // Some nav items are hidden based on `dashboardMode`
  const [searchParams] = useSearchParams();
  const dashboardMode = useMemo(() => {
    const rawDashboardMode = searchParams.get('mode');
    if (!rawDashboardMode) return 'current';
    if (rawDashboardMode === 'current') return 'current';
    if (rawDashboardMode === 'preview') return 'preview';
    return 'historical';
  }, [searchParams]);
  const dashboardModeQueryParam = useMemo(
    () => createSearchParams({ mode: dashboardMode }).toString(),
    [dashboardMode],
  );

  // Some routes have 2 headers, some 1 header. Navbar needs to adjust its height to match
  const hasSubheader = useCanToggleDataSource();

  // Some nav items are hidden based on special Valley user permissions
  const permissions = useMemo(
    () => getUserRoleTypeActionPermissions(user.roleType),
    [user.roleType],
  );

  return useMemo(
    () => ({
      dashboardMode,
      dashboardModeQueryParam,
      hasSubheader,
      permissions,
    }),
    [dashboardMode, dashboardModeQueryParam, hasSubheader, permissions],
  );
};
